import { useCallback, useMemo, useRef, useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import { ConfirmContext } from './ConfirmContext';

/**
 * Single-slot confirm-dialog host. Mounted once in `app/providers/index.jsx`
 * alongside `ToastContainer`. Holds at most one pending confirmation at a
 * time — a second call while one is open queues behind it.
 */
export function ConfirmProvider({ children }) {
    const [request, setRequest] = useState(null); // { options, resolve }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const queueRef = useRef([]);

    const openNext = useCallback(() => {
        const next = queueRef.current.shift();
        setError(null);
        setLoading(false);
        setRequest(next || null);
    }, []);

    const confirm = useCallback(
        (options) =>
            new Promise((resolve) => {
                const item = { options, resolve };
                queueRef.current.push(item);
                setRequest((current) => {
                    if (current) return current; // something is already open — stays queued
                    queueRef.current.shift();
                    setError(null);
                    setLoading(false);
                    return item;
                });
            }),
        [],
    );

    const handleCancel = useCallback(() => {
        if (loading) return;
        request?.resolve(false);
        openNext();
    }, [loading, request, openNext]);

    const handleConfirm = useCallback(async () => {
        if (!request) return;
        const { onConfirm } = request.options;
        if (!onConfirm) {
            request.resolve(true);
            openNext();
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await onConfirm();
            request.resolve(true);
            openNext();
        } catch (e) {
            setLoading(false);
            setError(e?.message || 'Something went wrong. Please try again.');
        }
    }, [request, openNext]);

    const value = useMemo(() => ({ confirm }), [confirm]);

    return (
        <ConfirmContext.Provider value={value}>
            {children}
            {request ? (
                <ConfirmDialog
                    title={request.options.title}
                    message={request.options.message}
                    details={request.options.details}
                    confirmLabel={request.options.confirmLabel}
                    cancelLabel={request.options.cancelLabel}
                    tone={request.options.tone}
                    stacked={request.options.stacked}
                    loading={loading}
                    error={error}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                />
            ) : null}
        </ConfirmContext.Provider>
    );
}

export default ConfirmProvider;
